import { useTranslation } from 'react-i18next'
import { Box, Button, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

const classes = {
    root: {
        background: 'background.paper',
        border: (theme) => `1px dashed ${theme.palette.secondary.main}`,
        borderRadius: 2,
        cursor: 'pointer',
        minWidth: '300px',
        maxWidth: '460px',
        flex: 1,
    },
}

/**
 * A dashed-border card prompting the user to start a new order via a given method.
 * @param {Object} props
 * @param {string} props.buttonText - Label for the action button.
 * @param {string} [props.helperText] - Optional explanatory text shown below the button.
 * @param {React.ReactElement} [props.icon] - Optional icon rendered at the top of the card.
 * @param {boolean} [props.disabled=false] - When true, the action button is disabled.
 * @param {string} [props.disabledMessage] - When disabled, shown as a tooltip on hover (e.g. why it's disabled).
 * @param {Function} [props.onButtonClick] - Click handler for the action button.
 * @param {string} props.title - Heading text for the card.
 * @returns {React.ReactElement}
 */
const AddItemCard = ({
    buttonText,
    disabled = false,
    disabledMessage = null,
    helperText = null,
    icon = null,
    onButtonClick,
    title,
}) => {
    const { t } = useTranslation()

    const button = (
        <Button
            color="primary"
            disabled={disabled}
            size="medium"
            variant="contained"
            onClick={onButtonClick}
        >
            {t(buttonText)}
        </Button>
    )

    return (
        <Box
            sx={[
                {
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                    padding: 3,
                    gap: 1.5,
                },
                classes.root,
            ]}
        >
            {icon ? <Box>{icon}</Box> : null}
            <Box
                sx={[
                    {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    },
                    classes.dropTextContainer,
                ]}
            >
                {title ? (
                    <Typography
                        align="center"
                        variant="body1"
                    >
                        {t(title)}
                    </Typography>
                ) : null}

                {disabled && disabledMessage ? (
                    <TbxTooltip
                        title={t(disabledMessage)}
                        arrow
                    >
                        <span>{button}</span>
                    </TbxTooltip>
                ) : (
                    button
                )}
                {helperText ? (
                    <Typography
                        align="center"
                        variant="small"
                    >
                        {t(helperText)}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    )
}

AddItemCard.propTypes = {
    buttonText: PropTypes.string,
    disabled: PropTypes.bool,
    disabledMessage: PropTypes.string,
    helperText: PropTypes.string,
    icon: PropTypes.element,
    title: PropTypes.string,
    onButtonClick: PropTypes.func,
}

export default AddItemCard
